import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listApps,
  createApp,
  deleteApp,
  renameApp,
  getCollaborators,
  addCollaborator,
  updateCollaborator,
  removeCollaborator,
  transferApp,
  createAppTransfer,
  listAppTransfers,
  acceptAppTransfer,
  cancelAppTransfer,
  type App,
  type AssignableRole,
  type CollaboratorMapResponse,
  type AppTransferCreateResponse,
  type AppTransferListResponse,
} from "../api/apps.api";
import { ApiError } from "@/lib/api/client";
import { accessFrom, type AppAccess } from "../lib/capabilities";

export const appsKeys = {
  all: ["apps"] as const,
  list: () => [...appsKeys.all, "list"] as const,
  collaborators: (appName: string) => [...appsKeys.all, "collaborators", appName] as const,
  transfers: () => [...appsKeys.all, "transfers"] as const,
};

export function useApps() {
  return useQuery<{ apps: App[] }, ApiError>({
    queryKey: appsKeys.list(),
    queryFn: listApps,
  });
}

export function useAppAccess(appName: string): AppAccess {
  const { data } = useApps();
  const app = data?.apps.find((candidate) => candidate.name === appName);
  if (!app) {
    return { serverCapabilities: false, can: () => false };
  }
  return accessFrom(app.capabilities, app.collaborators);
}

export function useCreateApp() {
  const queryClient = useQueryClient();

  return useMutation<{ app: App }, ApiError, { name: string }>({
    mutationFn: createApp,
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
      queryClient.invalidateQueries({ queryKey: ["deployments", name] });
    },
  });
}

export function useDeleteApp() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: deleteApp,
    onSuccess: (_, appName) => {
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
      queryClient.removeQueries({ queryKey: ["deployments", appName] });
    },
  });
}

export function useRenameApp() {
  const queryClient = useQueryClient();

  return useMutation<{ app: App }, ApiError, { appName: string; newName: string }>({
    mutationFn: ({ appName, newName }) => renameApp(appName, newName),
    onSuccess: (_, { appName, newName }) => {
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
      queryClient.invalidateQueries({ queryKey: ["deployments", newName] });
      queryClient.removeQueries({ queryKey: ["deployments", appName] });
    },
  });
}

export function useCollaborators(appName: string) {
  return useQuery<CollaboratorMapResponse, ApiError>({
    queryKey: appsKeys.collaborators(appName),
    queryFn: () => getCollaborators(appName),
  });
}

export function useAddCollaborator(appName: string) {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { email: string; permission?: AssignableRole }>({
    mutationFn: ({ email, permission }) => addCollaborator(appName, email, permission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appsKeys.collaborators(appName) });
      queryClient.invalidateQueries({ queryKey: appsKeys.list() });
    },
  });
}

export function useUpdateCollaborator(appName: string) {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { email: string; permission: AssignableRole }>({
    mutationFn: ({ email, permission }) => updateCollaborator(appName, email, permission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
      queryClient.invalidateQueries({ queryKey: ["deployments", appName] });
    },
  });
}

export function useRemoveCollaborator(appName: string) {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (email) => removeCollaborator(appName, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appsKeys.collaborators(appName) });
      queryClient.invalidateQueries({ queryKey: appsKeys.list() });
    },
  });
}

export function useTransferApp() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { appName: string; email: string }>({
    mutationFn: ({ appName, email }) => transferApp(appName, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
    },
  });
}

export function useAppTransfers() {
  return useQuery<AppTransferListResponse, ApiError>({
    queryKey: appsKeys.transfers(),
    queryFn: listAppTransfers,
  });
}

export function useCreateAppTransfer() {
  const queryClient = useQueryClient();

  return useMutation<AppTransferCreateResponse, ApiError, { appName: string; email: string }>({
    mutationFn: ({ appName, email }) => createAppTransfer(appName, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appsKeys.transfers() });
    },
  });
}

export function useAcceptAppTransfer() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: acceptAppTransfer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appsKeys.all });
      queryClient.invalidateQueries({ queryKey: ["deployments"] });
    },
  });
}

export function useCancelAppTransfer() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: cancelAppTransfer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appsKeys.transfers() });
    },
  });
}
